import {createBrowserRouter, Navigate} from "react-router-dom";
import {AuthenticationLayout} from "../Components/Layouts/AuthenticationLayout";
import {Login} from "../Views/Login";
import React from "react";

import {ForgotPassword} from "../Views/Authentication/ForgotPassword";
import {RegisterFreelancer} from "../Views/Authentication/RegisterFreelancer";
import {RegisterCustomer} from "../Views/Authentication/RegisterCustomer";
import {AdminLayout} from "../Components/Layouts/AdminLayout";
import {Error404} from "../Views/Errors/Error404";
import PendingApproval from "../Views/Authentication/PendingApproval";
import FreelancerApprovalGuard from "../Views/Authentication/FreelancerApprovalGuard";
import RoleGuard from "../Views/Authentication/RoleGuard";

import {MyProfile} from "../Views/Freelance/MyProfile";
import {MyContracts} from "../Views/Freelance/MyContracts";
import {MyPerformanceReports} from "../Views/Freelance/MyPerformanceReports";
import {MyApplications} from "../Views/Freelance/MyApplications";
import {Shifts} from "../Views/Freelance/Shifts";
import {MyAvailabilities} from "../Views/Freelance/MyAvailabilities";
import {MyInvoices} from "../Views/Freelance/MyInvoices";
import {ShiftDetail} from "../Views/Freelance/ShiftDetail";

import {MyProfile as MyProfileCustomer} from "../Views/Customer/MyProfile";
import {Shifts as ShiftsCustomer} from "../Views/Customer/Shifts";
import {MyCollaborations as MyApplicationsCustomer} from "../Views/Customer/MyCollaborations";
import {MyContracts as MyContractsCustomer} from "../Views/Customer/MyContracts";
import {MyInvoices as MyInvoicesCustomer} from "../Views/Customer/MyInvoices";
import {MyPerformanceReports as MyPerformanceReportsCustomer} from "../Views/Customer/MyPerformanceReports";

import {Registrations as AdminRegistrations} from "../Views/Admin/Registrations";
import {Applications as AdminApplications} from "../Views/Admin/Applications";
import {Customers as AdminCustomers} from "../Views/Admin/Customers";
import {Freelancers as AdminFreelancers} from "../Views/Admin/Freelancers";
import {Shifts as AdminShifts} from "../Views/Admin/Shifts";
import {Statistics as AdminStatistics} from "../Views/Admin/Statistics";
import {useBoundStore} from "../Store/store";
import {ShiftApplications} from "../Views/Customer/ShiftApplications";
import {ShiftApplicationsResponse} from "../Views/Customer/ShiftApplicationsResponse";
import {AdminShiftDetails} from "../Views/Admin/Details/AdminShiftDetails";
import {AdminCustomerDetails} from "../Views/Admin/Details/CustomerDetail";
import {AdminBranchDetails} from "../Views/Admin/Details/BranchDetail";
import {AdminFreelancerDetails} from "../Views/Admin/Details/FreelancerDetail";
import {AdminApplicationDetail} from "../Views/Admin/Details/ApplicationDetail";
import {MyApplicationDetail} from "../Views/Freelance/MyApplicationDetail";
import {AdminPerformanceStatementDetail} from "../Views/Admin/Details/PerformanceStatementDetail";
import {AdminContractDetail} from "../Views/Admin/Details/ContractDetail";
import {EnterNewPassword} from "../Views/Authentication/EnterNewPassword";
import {CustomerContractDetail} from "../Views/Customer/ContractDetail";
import {ApplicationDetail} from "../Views/Customer/Details/ApplicationDetail";
import {BulkMail} from "../Views/Admin/BulkMail";
import {AcceptShiftChangeRequest} from "../Views/General/AcceptShiftChangeRequest";
import {RejectShiftChangeRequest} from "../Views/General/RejectShiftChangeRequest";
import {AdminInvoiceDetail} from "../Views/Admin/Details/InvoiceDetail";
import {Invoices as AdminInvoices} from "../Views/Admin/Invoices";
import {ErrorFallback} from "../Views/Errors/ErrorFallback";

const AppRouter = createBrowserRouter([
    {
        path: "/",
        element: <AuthenticationLayout />,
        children: [
            {path: "", element: <Login />},
            {path: "forgot-password", element: <ForgotPassword />},
        ]
    },
    {
        path: "/login",
        element: <Navigate to={'/'} replace={true} />,
    },
    {
        path: "/register/freelancer",
        element: <AuthenticationLayout />,
        children: [
            {path: "", element: <RegisterFreelancer />},
        ]
    },
    {
        path: "/register/customer",
        element: <AuthenticationLayout />,
        children: [
            {path: "", element: <RegisterCustomer />},
        ]
    },
    {
        path: "/password/reset/:token",
        element: <AuthenticationLayout />,
        children: [
            {path: "", element: <EnterNewPassword />},
        ]
    },
    {
        path: "/change-request/:id",
        element: <AuthenticationLayout />,
        children: [
            {path: "accept", element: <AcceptShiftChangeRequest />},
            {path: "reject", element: <RejectShiftChangeRequest />},
        ]
    },
    {
        path: "/freelancer",
        errorElement: <ErrorFallback />,
        element: (
            <RoleGuard allow={['freelancer', 'admin']}>
                <FreelancerApprovalGuard>
                    <AdminLayout />
                </FreelancerApprovalGuard>
            </RoleGuard>
        ),
        children: [
            {path: "", element: <Navigate to={'/freelancer/shifts'} replace={true} />},
            {path: "profile", element: <MyProfile />},

            {path: "contracts", element: <MyContracts />},
            {path: "contracts/:id", element: <MyContracts />},

            {path: "invoices", element: <MyInvoices />},
            {path: "invoices/:id", element: <MyInvoices />},

            {path: "performance-report", element: <MyPerformanceReports />},
            {path: "performance-reports/:id", element: <AdminPerformanceStatementDetail />},

            {path: "applications", element: <MyApplications />},
            {path: "applications/:id", element: <MyApplicationDetail />},

            {path: "shifts", element: <Shifts />},
            {path: "shifts/:id", element: <Shifts />},

            {path: "availabilities", element: <MyAvailabilities />},
            {path: "availabilities/shifts/:shiftId", element: <ShiftDetail />},

            {path: "*", element: <Navigate to={'/freelancer/shifts'} replace={true} />},

            {path: "contracts/:id", element: <AdminContractDetail />},
        ]
    },
    {
        path: "/pending-approval",
        element: <AuthenticationLayout />,
        children: [
            { path: "", element: <PendingApproval /> },
        ]
    },
    {
        path: "/customer",
        errorElement: <ErrorFallback />,
        element: (
            <RoleGuard allow={['customer', 'admin']}>
                <AdminLayout />
            </RoleGuard>
        ),
        children: [
            {path: "profile", element: <MyProfileCustomer />},
            {path: "shifts", element: <ShiftsCustomer />},
            {path: "shifts/create", element: <Shifts />},
            {path: "shifts/:shiftId/applications", element: <ShiftApplications />},
            {path: "shifts/:shiftId/applications/:applicationId", element: <ShiftApplicationsResponse />},
            {path: "applications", element: <MyApplicationsCustomer />},
            {path: "application/:applicationId", element: <ApplicationDetail />},
            {path: "contracts", element: <MyContractsCustomer />},
            {path: "contracts/:contractId", element: <CustomerContractDetail />},
            {path: "payments", element: <MyInvoicesCustomer />},
            {path: "performance-reports", element: <MyPerformanceReportsCustomer />},

            {path: "performance-reports/:id", element: <AdminPerformanceStatementDetail />},
            {path: "contracts/:id", element: <AdminContractDetail />},
        ]
    },
    {
        path: "/admin",
        errorElement: <ErrorFallback />,
        element: (
            <RoleGuard allow={['admin']}>
                <AdminLayout />
            </RoleGuard>
        ),
        children: [
            {path: "registrations", element: <AdminRegistrations />},

            {path: "applications", element: <AdminApplications />},
            {path: 'applications/:applicationId', element: <AdminApplicationDetail />},

            {path: "customers", element: <AdminCustomers />},
            {path: "customers/:customerId", element: <AdminCustomerDetails />},
            {path: "customers/:customerId/branches/:branchId", element: <AdminBranchDetails />},

            {path: "freelancers", element: <AdminFreelancers />},
            {path: "freelancers/:freelancerId", element: <AdminFreelancerDetails />},

            {path: "statistics", element: <AdminStatistics />},

            {path: "shifts", element: <AdminShifts />},
            {path: "shifts/:shiftId", element: <AdminShiftDetails />},

            {path: "invoices", element: <AdminInvoices />},
            {path: "performance-reports/:id", element: <AdminPerformanceStatementDetail />},
            {path: "invoices/:id", element: <AdminInvoiceDetail />},
            {path: "contracts/:id", element: <AdminContractDetail />},

            {path: 'bulk-mail', element: <BulkMail />},
        ]
    },

    {
        path: "logout",
        element: <Navigate to={'/'} replace={true} />,
        loader: () => {
            useBoundStore.getState().logOut();
            return null;
        },
    },

    {path: "*", element: <Error404 />},
]);

export default AppRouter;
